// Pieces copied from the approved boards in _bmad-output/specs/spec-personal-spaces/design/.
import React from 'react';
import { t } from '../../i18n';
import { textDir } from '../../i18n/direction';

export const isBlank = text => text.trim().length === 0;

// Every board's interactive links are <a>; they never leave the page here.
export const onTap = action => e => {
  e.preventDefault();
  action();
};

export const Logo = ({ width, height, line = '#24102a', style }) => (
  <svg width={width} height={height} viewBox="0 0 64 64" aria-hidden="true" style={style}>
    <g transform="rotate(-10 23 33)">
      <rect x="6" y="10" width="34" height="46" rx="6" fill="#4b1d52" stroke={line} strokeWidth="2"></rect>
      <circle cx="11.5" cy="27" r="1.8" fill="#fff6e0"></circle>
      <circle cx="17.5" cy="27" r="1.8" fill="#fff6e0"></circle>
      <path d="M12 33l2.5 6 2.5-6" fill="none" stroke="#fff6e0" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"></path>
    </g>
    <g transform="rotate(8 39 31)">
      <rect x="22" y="8" width="34" height="46" rx="6" fill="#ffffff" stroke={line} strokeWidth="2"></rect>
      <circle cx="33.5" cy="23" r="2.4" fill="#24102a"></circle>
      <circle cx="44.5" cy="23" r="2.4" fill="#24102a"></circle>
      <rect x="30" y="32" width="18" height="4" rx="2" fill="#ee5a4a"></rect>
    </g>
  </svg>
);

// HomeJoinError / SignInNotInvited warning mark.
export const WarnIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#c7392a" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M12 3l10 18H2z" strokeLinejoin="round"></path><path d="M12 10v5M12 18v.01"></path></svg>
);

// AccountUsernameTaken warning mark.
export const WarnFilledIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ee5a4a" stroke="#24102a" strokeWidth="2" strokeLinejoin="round" aria-hidden="true"><path d="M12 3l10 18H2z"></path><path d="M12 10v5M12 18v.01" fill="none" strokeLinecap="round" strokeWidth="2.4"></path></svg>
);

export const GeneralError = () => (
  <p className="err" role="alert"><WarnIcon />{t('error.general')}</p>
);

// The two-colour game name; each screen keeps its own size and accent colour.
export const Wordmark = ({ accent }) => (
  <>{t('wordmark.lead')}<span style={{ color: accent }}>{t('wordmark.accent')}</span></>
);

export const PersonIcon = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="8" r="4"></circle><path d="M4 21c0-4 4-6.5 8-6.5s8 2.5 8 6.5"></path></svg>
);

// The language menu: the globe (22px) and the account menu's language row (20px).
export const GlobeIcon = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M3 12h18"></path><path d="M12 3c2.8 2.6 4 5.6 4 9s-1.2 6.4-4 9c-2.8-2.6-4-5.6-4-9s1.2-6.4 4-9z"></path></svg>
);

export const SignOutIcon = () => (
  <svg className="icon-dir" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10 4H5v16h5"></path><path d="M14 8l-4 4 4 4"></path><path d="M10 12h10"></path></svg>
);

// Level tag of a share: MyDecks, SharedView, SharedEdit, ShareSheet.
export const LevelTag = ({ level }) => (level === 'edit'
  ? <span className="tag tag-edit">{t('share.edit')}</span>
  : <span className="tag">{t('share.view')}</span>);

export const BackIcon = () => (
  <svg className="icon-dir" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 5l7 7-7 7"></path></svg>
);

// MyDecks (20px) and the blank button of the question boards (18px).
export const PlusIcon = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"></path></svg>
);

export const Spinner = () => (
  <svg className="spin" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity=".25" strokeWidth="3"></circle><path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round"></path></svg>
);

// Google's standard multicolour "G" mark (canvas note n9: replaces the board's dashed placeholder).
export const GoogleMark = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
  </svg>
);

// The board's nickname field ("Who am I" with an N/40 count), shared by every join and host screen.
// HomeJoinNameTaken: "Name taken" right after the input, before the hint (order of AccountUsernameTaken).
export const NicknameField = ({ value, onChange, locked, taken, withHint }) => (
  <div className="field">
    <label className="label" htmlFor="name"><span>{t('home.name')}</span><span className="count">{t('common.counter', { n: value.length, max: 40 })}</span></label>
    <input id="name" className={locked ? 'input input-locked' : taken ? 'input input-error' : 'input'} value={value} maxLength={40} dir={textDir(value)}
      disabled={locked} aria-invalid={taken ? 'true' : undefined} aria-describedby={taken ? 'name-err' : undefined}
      onChange={e => onChange(e.target.value.substring(0, 40))} />
    {taken && <div id="name-err" className="err" role="alert"><WarnIcon />{t('error.nameTaken')}</div>}
    {withHint && <p className="hint">{t('home.nameHint')}</p>}
  </div>
);

// HomeJoinBlocked: too many wrong codes locks the code field until the server's Retry-After passes.
export const CodeField = ({ value, onChange, notFound, blocked, placeholder }) => {
  const error = blocked ? t('error.blocked') : notFound ? t('error.gameNotFound') : null;
  return (
    <div className="field">
      <label className="label" htmlFor="code">{t('home.code')}</label>
      <input id="code" className={blocked ? 'input input-code input-locked' : notFound ? 'input input-code input-error' : 'input input-code'}
        value={value} maxLength={4} placeholder={placeholder} autoCapitalize={placeholder ? 'characters' : undefined}
        disabled={blocked} aria-invalid={error && !blocked ? 'true' : undefined} aria-describedby={error ? 'code-err' : undefined}
        onChange={e => onChange(e.target.value.substring(0, 4).toUpperCase())} />
      {error && <div id="code-err" className="err" role="alert"><WarnIcon />{error}</div>}
    </div>
  );
};

// Main button: enabled = the board's class; blank input = btn-disabled with aria-disabled and the click ignored.
export const MainButton = ({ enabledClass, disabled, onClick, children, ...rest }) => (
  <button type="button" className={disabled ? 'btn btn-disabled btn-block' : `btn ${enabledClass} btn-block`}
    aria-disabled={disabled ? 'true' : undefined} onClick={disabled ? undefined : onClick} {...rest}>{children}</button>
);
